import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StarRating } from '../components/ReviewsSection';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, MapPin, Building2, Globe, Loader2, Eye, User, Star, Map, List, Navigation, Lock, Plane } from 'lucide-react';
import axios from 'axios';
import DirectoryMap from '../components/DirectoryMap';
import BannerCard from '../components/BannerCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALIZATIONS = ['mechatronics', 'mechanical', 'electrical', 'software'];
const REGIONS = ['europe', 'asia', 'america', 'south_america', 'africa'];
const ENTITY_TYPES = ['freelancer', 'company'];

const DirectoryPage = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated, hasPaid, isProfessional, user, loading: authLoading } = useAuth();
  const isFreeCompany = user && ['azienda_manifatturiera', 'azienda_automazione'].includes(user.user_type) && (user.subscription_plan || 'free') === 'free';
  const navigate = useNavigate();
  
  const [professionals, setProfessionals] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [specFilter, setSpecFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [userLocation, setUserLocation] = useState(null);
  const [radiusKm, setRadiusKm] = useState('50');
  const [nearbyMode, setNearbyMode] = useState(false);
  const [locatingUser, setLocatingUser] = useState(false);

  // Check access — anyone authenticated can see the directory (data is masked for free companies)
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate, authLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      if (nearbyMode && userLocation) {
        fetchNearbyProfessionals();
      } else {
        fetchProfessionals();
      }
    }
  }, [specFilter, regionFilter, entityFilter, isAuthenticated, nearbyMode, userLocation, radiusKm]);

  // Fetch active banners once, filtered by current UI language
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBanners = async () => {
      try {
        const res = await axios.get(`${API}/banners/active`, { params: { lang: language } });
        setBanners(res.data || []);
      } catch (_e) {
        setBanners([]);
      }
    };
    fetchBanners();
  }, [isAuthenticated, language]);

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (specFilter !== 'all') params.append('specialization', specFilter);
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/professionals?${params.toString()}`);
      setProfessionals(response.data);
    } catch (error) {
      console.error('Error fetching professionals:', error);
      // In the new free model, a 403 here is unexpected — just stop loading
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyProfessionals = async () => {
    if (!userLocation) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('lat', userLocation.lat);
      params.append('lng', userLocation.lng);
      params.append('radius_km', radiusKm);
      if (specFilter !== 'all') params.append('specialization', specFilter);
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      
      const response = await axios.get(`${API}/professionals/nearby?${params.toString()}`);
      setProfessionals(response.data);
    } catch (error) {
      console.error('Error fetching nearby professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocateMe = () => {
    setLocatingUser(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setNearbyMode(true);
          setLocatingUser(false);
        },
        () => {
          setLocatingUser(false);
          alert('Impossibile ottenere la posizione. Verifica le impostazioni del browser.');
        },
        { enableHighAccuracy: false, timeout: 10000 }
      );
    } else {
      setLocatingUser(false);
      alert('Geolocalizzazione non supportata dal browser.');
    }
  };

  const handleClearNearby = () => {
    setNearbyMode(false);
    setUserLocation(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfessionals();
  };

  const getSpecBadgeColor = (spec) => {
    const colors = {
      mechatronics: 'bg-orange-100 text-orange-700 border-orange-200',
      mechanical: 'bg-blue-100 text-blue-700 border-blue-200',
      electrical: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      software: 'bg-green-100 text-green-700 border-green-200',
    };
    return colors[spec] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="directory-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('dir_title')}
          </h1>
        </div>

        {/* Upgrade banner for free companies */}
        {isFreeCompany && (
          <div
            className="mb-6 rounded-lg bg-gradient-to-r from-slate-900 to-slate-700 text-white p-6 flex flex-col md:flex-row items-start md:items-center gap-4 shadow-lg"
            data-testid="upgrade-banner"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
              <Lock className="h-6 w-6 text-orange-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{t('dir_upgrade_title')}</h3>
              <p className="text-sm text-slate-300">{t('dir_upgrade_subtitle')}</p>
            </div>
            <Button
              onClick={() => navigate('/subscription?upgrade=1')}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-md px-6 py-5 font-semibold whitespace-nowrap"
              data-testid="upgrade-cta"
            >
              {t('dir_upgrade_cta')}
            </Button>
          </div>
        )}
        
        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('dir_search')}
                className="pl-10 rounded-sm"
                data-testid="directory-search"
              />
            </div>
            
            {/* Filter Row */}
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={specFilter} onValueChange={setSpecFilter}>
                <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="filter-spec">
                  <SelectValue placeholder={t('dir_filter_spec')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('dir_all')}</SelectItem>
                  {SPECIALIZATIONS.map((spec) => (
                    <SelectItem key={spec} value={spec}>
                      {t(`spec_${spec}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="filter-region">
                  <SelectValue placeholder={t('dir_filter_region')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('dir_all')}</SelectItem>
                  {REGIONS.map((region) => (
                    <SelectItem key={region} value={region}>
                      {t(`region_${region}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="filter-entity">
                  <SelectValue placeholder={t('dir_filter_entity')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('dir_all')}</SelectItem>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`entity_${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                data-testid="search-submit"
              >
                <Search className="h-4 w-4 mr-2" />
                {t('dir_search').split('...')[0]}
              </Button>
            </div>

            {/* Proximity + View Toggle Row */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 pt-2 border-t border-slate-100">
              {/* Nearby search */}
              <div className="flex items-center gap-2">
                {!nearbyMode ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                    onClick={handleLocateMe}
                    disabled={locatingUser}
                    data-testid="locate-me-btn"
                  >
                    {locatingUser ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Navigation className="h-4 w-4" />
                    )}
                    Cerca vicino a me
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                      <Navigation className="h-3 w-3 mr-1" />
                      Ricerca per vicinanza attiva
                    </Badge>
                    <Select value={radiusKm} onValueChange={setRadiusKm}>
                      <SelectTrigger className="w-32 h-8 text-sm rounded-sm" data-testid="radius-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 km</SelectItem>
                        <SelectItem value="25">25 km</SelectItem>
                        <SelectItem value="50">50 km</SelectItem>
                        <SelectItem value="100">100 km</SelectItem>
                        <SelectItem value="200">200 km</SelectItem>
                        <SelectItem value="500">500 km</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={handleClearNearby}
                      data-testid="clear-nearby-btn"
                    >
                      Annulla
                    </Button>
                  </div>
                )}
              </div>

              {/* View mode toggle */}
              <div className="flex items-center gap-1 ml-auto bg-slate-100 rounded-sm p-0.5">
                <Button
                  type="button"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className={`h-7 px-3 rounded-sm ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                >
                  <List className="h-4 w-4 mr-1" />
                  Lista
                </Button>
                <Button
                  type="button"
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  className={`h-7 px-3 rounded-sm ${viewMode === 'map' ? 'bg-white shadow-sm' : ''}`}
                  onClick={() => setViewMode('map')}
                  data-testid="view-map-btn"
                >
                  <Map className="h-4 w-4 mr-1" />
                  Mappa
                </Button>
              </div>
            </div>
          </form>
        </div>
        
        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t('dir_no_results')}</p>
          </div>
        ) : (
          <>
            {/* Map View */}
            {viewMode === 'map' && (
              <div className="mb-6">
                <DirectoryMap 
                  professionals={professionals} 
                  onProfessionalClick={(id) => navigate(`/professional/${id}`)}
                  height="450px"
                />
              </div>
            )}

            {/* List/Grid View */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionals.map((prof, idx) => {
              const isMasked = prof._is_masked === true;
              return (
              <React.Fragment key={prof.id}>
              <Card 
                className={`border rounded-md transition-colors ${isMasked ? 'border-slate-300 bg-slate-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`prof-card-${prof.id}`}
                data-masked={isMasked}
              >
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                      {!isMasked && prof.logo_url ? (
                        <img 
                          src={prof.logo_url} 
                          alt={prof.company_name}
                          className="w-14 h-14 object-cover rounded-md border border-slate-200"
                        />
                      ) : (
                        <div className={`w-14 h-14 rounded-md flex items-center justify-center ${isMasked ? 'bg-slate-200' : 'bg-slate-100'}`}>
                          {isMasked ? (
                            <Lock className="h-6 w-6 text-slate-400" />
                          ) : prof.entity_type === 'freelancer' ? (
                            <User className="h-6 w-6 text-slate-400" />
                          ) : (
                            <Building2 className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isMasked ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                            ✓ {t('dir_verified_label')}
                          </Badge>
                        ) : (
                          <>
                            <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">
                              ID: {prof.id.slice(0, 8)}
                            </span>
                            {prof.entity_type && (
                              <Badge variant="outline" className={`text-xs ${
                                prof.entity_type === 'freelancer' 
                                  ? 'bg-purple-100 text-purple-700 border-purple-200'
                                  : 'bg-slate-100 text-slate-700 border-slate-200'
                              }`}>
                                {t(`entity_${prof.entity_type}`)}
                              </Badge>
                            )}
                          </>
                        )}
                        {prof.available_for_travel && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs gap-1">
                            <Plane className="h-3 w-3" />
                            {t('dir_trasfertista_badge')}
                          </Badge>
                        )}
                      </div>
                      <h3 
                        className="text-lg font-semibold text-slate-900 truncate"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {isMasked ? t('dir_masked_company') : prof.company_name}
                      </h3>
                      <p className="text-sm text-slate-600 truncate">
                        {isMasked ? t('dir_masked_name') : prof.name}
                      </p>
                    </div>
                  </div>

                  {/* Rating */}
                  {prof.review_count > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <StarRating rating={Math.round(prof.average_rating || 0)} readOnly size="sm" />
                      <span className="text-sm text-slate-500">
                        ({prof.review_count})
                      </span>
                    </div>
                  )}
                  
                  {/* Location */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>
                      {isMasked
                        ? (prof.country || t('dir_masked_area'))
                        : `${prof.location_city || prof.city}${prof.country ? `, ${prof.country}` : ''}`}
                    </span>
                    {prof.distance_km !== undefined && (
                      <Badge className="ml-auto bg-orange-100 text-orange-700 border-orange-200 text-xs">
                        {prof.distance_km} km
                      </Badge>
                    )}
                  </div>
                  
                  {/* Specializations */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {Object.keys(prof.specializations || {}).map((spec) => (
                      <Badge 
                        key={spec} 
                        variant="outline"
                        className={`text-xs ${getSpecBadgeColor(spec)}`}
                      >
                        {t(`spec_${spec}`)}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Regions */}
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                    <Globe className="h-4 w-4" />
                    <span className="truncate">
                      {(prof.regions || []).map((r) => t(`region_${r}`)).join(', ')}
                    </span>
                  </div>

                  {/* Stats — only when not masked */}
                  {!isMasked && (
                    <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {prof.profile_views}
                      </div>
                      {prof.review_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3" />
                          {prof.average_rating?.toFixed(1)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  {isMasked ? (
                    <Button
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-sm text-sm font-semibold py-5 gap-2"
                      onClick={() => navigate('/subscription?upgrade=1')}
                      data-testid={`unlock-${prof.id}`}
                    >
                      <Lock className="h-4 w-4" />
                      {t('dir_unlock_with_premium')}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        className="flex-1 rounded-sm text-sm"
                        onClick={() => navigate(`/professional/${prof.id}`)}
                        data-testid={`view-profile-${prof.id}`}
                      >
                        {t('dir_view_profile')}
                      </Button>
                      {isProfessional && (
                        <Button 
                          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-sm"
                          onClick={() => navigate(`/messages?to=${prof.user_id}`)}
                          data-testid={`contact-${prof.id}`}
                        >
                          {t('dir_contact')}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              {banners.length > 0 && (idx + 1) % 5 === 0 && (
                <BannerCard
                  banner={banners[Math.floor(idx / 5) % banners.length]}
                  sponsoredLabel={t('banner_sponsored')}
                  ctaLabel={t('banner_cta')}
                />
              )}
              </React.Fragment>
              );
            })}
            {/* If less than 5 pros, ensure at least one banner is shown */}
            {banners.length > 0 && professionals.length > 0 && professionals.length < 5 && (
              <BannerCard
                key={`banner-fallback-${banners[0].id}`}
                banner={banners[0]}
                sponsoredLabel={t('banner_sponsored')}
                ctaLabel={t('banner_cta')}
              />
            )}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DirectoryPage;
